/* eslint-disable camelcase */

import React from 'react';
import { Chip, Grid, Typography } from '@mui/material';
import { DEFAULT_TAX_STRATEGY } from 'types/TaxStrategyType';
import BundlesType from 'types/BundlesType';
import ProductAccordion from 'components/ProductAccordion';

import Invoice from 'types/Invoice';
import isNonProduction from 'utilities/isNonProduction';
import { Luci_Shopping_Api_Models_OrderItem } from 'types/api/v7.1';
import RewardsPanel from '../../components/RewardsPanel/RewardsPanel';
import OrderItem from '../../types/OrderItem';

import CostCalculationTable from '../../components/CostCalculationTable';
import PayPalLogo from '../../images/PayPal_Logo_Horizontal_Full_Color_RGB.png';
import ProductLineItem from '../../components/ProductLineItem';
import styles from "./InvoicePortalConfirmation.module.scss";

type Props = {
  invoice: Invoice;
};

const InvoicePortalConfirmation = ({ invoice }: Props): JSX.Element => {
  const {
    invoiceDetails,
    shippingPolicy,
    returnPolicy,
    storeName,
    isCustomerRewardsEnabled,
    rewardsAvailable,
    totalPointsAccruedOnThisOrder,
  } = invoice;

  // Check if invoiceDetails exists before destructuring
  if (!invoiceDetails?.order) {
    return (
      <Typography marginBottom="4rem">
        Order details are not available.
      </Typography>
    );
  }

  const { receiptNotes, customer, orderItems, notes, transactionId } =
    invoiceDetails.order;

  if (!orderItems?.length) {
    return (
      <Typography marginBottom="4rem">
        There are no items associated with this order.
      </Typography>
    );
  }

  // Use the utility function to calculate total points
  const totalPointsOnOrder = totalPointsAccruedOnThisOrder ?? 0;
  const totalPointsAvailable = rewardsAvailable ?? 0;

  return (
    <>
      <Grid container className="my-4">
        <Grid
          item
          xs={12}
          paddingBottom=".25rem"
          marginBottom="1rem"
          sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.divider}` })}
        >
          <p>Hello {customer?.firstName},</p>
          <p className={styles.wordWrap}>
            {receiptNotes?.length
              ? receiptNotes
              : 'Thanks for shopping with us. We are processing your order now.'}
          </p>
          <p>
            Sincerely,
            <br />
            <span data-testid="store-name">{storeName}</span>
          </p>
        </Grid>
        {invoiceDetails?.order?.orderItems?.map((item: OrderItem) => (
          <div
            key={`${item.type}-${
              item.itemNumberRetailerAssigned ?? item.id ?? 'unassigned'
            }-${item.sku}`}
          >
            {isNonProduction && (
              <Chip
                label={`DEV ONLY: Reward value for ${
                  (item?.quantity ?? 0) > 0
                    ? (item?.quantity ?? 0) * (item?.rewardPointValue ?? 0)
                    : item?.rewardPointValue ?? 0
                }`}
                sx={{ fontWeight: 'bold' }}
              />
            )}
            {item.type === 'Bundle' ? (
              <ProductAccordion
                key={`${item.type}-${
                  item.itemNumberRetailerAssigned ?? item.id ?? 'unassigned'
                }-${item.sku}`}
                lineItem={item as BundlesType}
              />
            ) : (
              <ProductLineItem
                id={parseInt(item.itemNumberRetailerAssigned ?? '', 10) ?? undefined}
                key={`${item.type}-${
                  item.itemNumberRetailerAssigned ?? item.id ?? 'unassigned'
                }-${item.sku}`}
                type={item.type || Luci_Shopping_Api_Models_OrderItem.type.PRODUCT}
                image={item.image ?? undefined}
                description={item?.description || ''}
                name={item.name || ''}
                sku={item.sku || undefined}
                quantity={item.quantity ?? 0}
                isFree={item.isFree || false}
                isReward={item.isReward || false}
                salePrice={item.salePrice ?? 0}
              />
            )}
          </div>
        ))}
        <hr />
        <CostCalculationTable
          order={invoiceDetails.order}
          estimates={invoiceDetails}
          taxStrategy={invoiceDetails?.taxStrategy ?? DEFAULT_TAX_STRATEGY}
        />
        {transactionId && (
          <Grid item xs={12} marginBottom="2rem">
            <p>PayPal Transaction ID: {transactionId}</p>
            <div className="mt-4">
              <p className={styles.bold}>Payment Method</p>
              <img
                src={PayPalLogo}
                className={styles.paypalLogo}
                alt="PayPal"
              />
            </div>
          </Grid>
        )}
        {isCustomerRewardsEnabled && (
          <RewardsPanel
            totalPointsAccruedOnThisOrder={totalPointsOnOrder}
            pointsAvailable={totalPointsAvailable}
            rewardsAvailable={rewardsAvailable}
            rewardPointGoal={invoice.rewardPointGoal}
            pointsNeededForNextReward={invoice.pointsNeededForNextReward}
          />
        )}
        {returnPolicy?.policy && (
          <Grid
            item
            xs={12}
            paddingTop="1rem"
            sx={(theme) => ({ borderTop: `1px solid ${theme.palette.divider}` })}
          >
            <Typography component="h3" variant="h5">
              Return Policy
            </Typography>
            <p className={styles.wordWrap}>{returnPolicy.policy}</p>
          </Grid>
        )}
        {shippingPolicy?.policy && (
          <Grid item xs={12} marginBottom="1rem">
            <Typography component="h3" variant="h5">
              Shipping Policy
            </Typography>
            <p className={styles.wordWrap}>{shippingPolicy.policy}</p>
          </Grid>
        )}
        {notes && notes.length > 0 && (
          <Grid
            item
            xs={12}
            marginBottom="1rem"
            sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.divider}` })}
          >
            <Typography component="h3" variant="h5">
              Order Notes
            </Typography>
            <p className={styles.wordWrap}>{notes}</p>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default InvoicePortalConfirmation;
